/**
 * Scroll animations — portable utility classes you add to any element:
 *   .anim-fade-up          fade + rise in when scrolled into view
 *   .anim-fade-up-stagger  put on a WRAPPER; its direct children cascade in
 *   .anim-reveal-lines     line-by-line text reveal (uses SplitText)
 *   .anim-spin-scroll      rotation tied to scroll position (scrubbed), turning
 *                          as the element passes through the viewport. GSAP owns
 *                          the element's transform, so apply it to an element
 *                          that carries no other transform (wrap a positioned
 *                          element and spin the inner child).
 *
 * Per-element data attributes: data-anim-start (ScrollTrigger start, default
 * "15% bottom" — fires when the element is 15% into the viewport from the
 * bottom), data-anim-delay, data-anim-duration, data-anim-stagger,
 * data-anim-once. For .anim-spin-scroll: data-anim-degrees (total turn, default
 * 110 — negative for anticlockwise), data-anim-start/data-anim-end (defaults
 * "top bottom" / "bottom top", i.e. across the whole time it is on screen).
 *
 * GSAP, ScrollTrigger and SplitText load as classic deferred scripts in
 * snippets/scripts.liquid, ahead of this module. Deferred classic scripts and
 * module scripts both run after parsing in document order, so the three globals
 * exist by the time anything here does.
 *
 * That same snippet puts `anim` on <html> before first paint, which pre-hides
 * the three classes (the rule is in src/tailwind.css) so nothing paints at its
 * final position and then jumps back to animate in. `window.__animReady` tells
 * the snippet's safety timeout that this module ran; every path out of here
 * either hands the element to GSAP or drops the class, so content can't stay
 * hidden.
 */

const RISE = 30;
const DURATION = 1;
const STAGGER = 0.2;
const EASE = 'power2.out';
const SPIN_DEGREES = 110;

/**
 * Fire once the element itself is 15% into the viewport from the bottom — the
 * point 15% down the element reaches the viewport's bottom edge, i.e. shortly
 * after it starts to appear.
 */
const START = '15% bottom';

const { gsap, ScrollTrigger, SplitText } = window;
const hasSplit = Boolean(SplitText);

/** Drops the pre-hide class, so every .anim-* element shows as authored. */
const reveal = () => document.documentElement.classList.remove('anim');

window.__animReady = true;

const fadeUp = (elements) => {
  gsap.utils.toArray(elements).forEach((el) => {
    if (el.dataset.animDone === 'true') return;

    gsap.fromTo(
      el,
      { y: RISE, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        duration: DURATION,
        ease: EASE,
        delay: parseFloat(el.dataset.animDelay) || 0,
        scrollTrigger: {
          trigger: el,
          start: el.dataset.animStart || START,
          toggleActions: 'play none none none',
        },
        onComplete: () => {
          el.dataset.animDone = 'true';
        },
      }
    );
  });
};

/**
 * Container-based: put .anim-fade-up-stagger on a wrapper and its DIRECT
 * children cascade in from one ScrollTrigger on the wrapper. data-anim-stagger
 * sets the gap between children (default 0.2s).
 */
const fadeUpStagger = (elements) => {
  gsap.utils.toArray(elements).forEach((container) => {
    if (container.dataset.animDone === 'true') return;

    const children = gsap.utils.toArray(container.children);
    if (!children.length) return;

    // Reveal the wrapper (it was pre-hidden); the children start hidden via the
    // fromTo below, so there's no flash.
    gsap.set(container, { visibility: 'visible' });

    gsap.fromTo(
      children,
      { y: RISE, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        duration: parseFloat(container.dataset.animDuration) || DURATION,
        ease: EASE,
        delay: parseFloat(container.dataset.animDelay) || 0,
        stagger: parseFloat(container.dataset.animStagger) || STAGGER,
        scrollTrigger: {
          trigger: container,
          start: container.dataset.animStart || START,
          toggleActions: 'play none none none',
        },
        onComplete: () => {
          container.dataset.animDone = 'true';
        },
      }
    );
  });
};

const revealLines = (el) => {
  if (!hasSplit || el.dataset.animDone === 'true') return;

  const duration = parseFloat(el.dataset.animDuration) || 1;
  const stagger = parseFloat(el.dataset.animStagger) || 0.1;
  const once = el.dataset.animOnce !== 'false';

  el.style.display = 'block';

  const split = new SplitText(el, { type: 'lines', linesClass: 'line-child' });
  // Second pass wraps each line in its own clipping parent, so a line can slide
  // up from behind the line above it rather than from outside the element.
  new SplitText(el, { type: 'lines', linesClass: 'line-parent' });
  const parents = el.querySelectorAll('.line-parent');
  gsap.set(parents, { overflow: 'hidden', display: 'block' });

  // Each mask is one line box tall, which the display tokens set tighter than
  // their font — so a descender would hang past the bottom edge and be cut, for
  // good, not just while the line moves. The .line-parent rule in
  // src/tailwind.css pads the mask downwards to clear that; read the padding
  // back and start the line the same distance lower, so it still begins fully
  // outside the taller mask. Nothing clips the heading itself, or that padding
  // would be cut off again on the last line.
  const overhang = parseFloat(getComputedStyle(parents[0]).paddingBlockEnd) || 0;

  // Reveal the parent (it may be pre-hidden); the lines start clipped below and
  // slide up into view.
  gsap.set(el, { visibility: 'visible' });

  gsap.from(split.lines, {
    yPercent: 100,
    y: overhang,
    duration,
    ease: EASE,
    stagger,
    scrollTrigger: {
      trigger: el,
      start: el.dataset.animStart || START,
      toggleActions: once ? 'play none none none' : 'play none none reverse',
    },
    onComplete: () => {
      el.dataset.animDone = 'true';
    },
  });
};

/**
 * Rotation scrubbed to scroll position: the element turns as it travels through
 * the viewport and turns back when you scroll up. GSAP writes the element's
 * transform, so keep any positioning/translate on a parent.
 */
const spinScroll = (elements) => {
  gsap.utils.toArray(elements).forEach((el) => {
    const degrees = parseFloat(el.dataset.animDegrees);

    gsap.to(el, {
      rotation: Number.isNaN(degrees) ? SPIN_DEGREES : degrees,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: el.dataset.animStart || 'top bottom',
        end: el.dataset.animEnd || 'bottom top',
        scrub: true,
      },
    });
  });
};

const setup = () => {
  gsap.registerPlugin(ScrollTrigger);
  if (hasSplit) gsap.registerPlugin(SplitText);

  fadeUp('.anim-fade-up');
  fadeUpStagger('.anim-fade-up-stagger');
  spinScroll('.anim-spin-scroll');
  gsap.utils.toArray('.anim-reveal-lines').forEach(revealLines);

  // Animate elements that get added later (e.g. Load More).
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (node.matches('.anim-fade-up')) fadeUp([node]);
        if (node.matches('.anim-fade-up-stagger')) fadeUpStagger([node]);

        const fades = node.querySelectorAll('.anim-fade-up');
        const staggers = node.querySelectorAll('.anim-fade-up-stagger');

        if (fades.length) {
          fadeUp(fades);
          ScrollTrigger.refresh();
        }
        if (staggers.length) {
          fadeUpStagger(staggers);
          ScrollTrigger.refresh();
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // The pre-hide class has done its job. Every element above was handed to a
  // fromTo/from tween, which writes its hidden start state inline, so dropping
  // the class now changes nothing that is waiting to animate.
  //
  // Leaving it on is what breaks: a section re-rendered after load (the product
  // page's recommendations, any Section Rendering API update) comes back as
  // server HTML with GSAP's inline style gone. The rule would still match it and
  // there is nothing left to reveal it, so the content stays invisible for good.
  // Anything arriving that late renders plainly instead, which is the right
  // failure.
  reveal();
};

if (!gsap || !ScrollTrigger || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  // No GSAP, or the visitor asked for no motion: show everything as authored.
  reveal();
} else if (document.readyState === 'complete') {
  setup();
} else {
  // SplitText measures line breaks, so wait for full load (fonts/images).
  window.addEventListener('load', setup);
}
